import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/shadcn/ui/dialog";
import { Button } from "components/shadcn/ui/button";
import { Label } from "components/shadcn/ui/label";
import { CardContent } from "components/shadcn/ui/card";
import { Alert } from "components/shadcn/ui/alert";
import { HelpCircle, LockIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/shadcn/ui/tooltip";
import lang from "lang";
import { useDomainSettingsDialog } from "./useDomainSettings";
import UnlockFullQuotaBanner from "components/subscription/unlockFullQoutaBanner";
import UnlockFullQuotaSuccessBanner from "components/subscription/unlockFullQuotaSuccessBanner";
import clsx from "clsx";
import {
  InputGroup,
  InputGroupInput,
  InputGroupAddon,
} from "components/shadcn/ui/input-group";
import { Input } from "components/shadcn/ui/input";
import { domainSetupKnowledgeBaseUrl } from "components/utils/network/endpoints";
import { TRIAL_USER_SOURCE } from "components/subscription/types";

interface DomainSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DomainSettingsDialog: React.FC<DomainSettingsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const { domainSettings: domainSettingsCopy } = lang;

  const {
    firstName,
    lastName,
    nameError,
    domain,
    domainError,
    apiError,
    successMessage,
    isTrial,
    isSaving,
    userNotifyTo,
    userData,
    subscriptionDetails,
    showSuccessEndTrialBanner,
    handleDomainChange,
    handleWebsiteBlur,
    handleWebsitePaste,
    handleSave,
    setFirstName,
    setLastName,
    handleSubscriptionActivatedSuccesfully,
  } = useDomainSettingsDialog({
    onClose: () => onOpenChange(false),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="min-w-[80%]">
        <DialogHeader>
          <DialogTitle>
            {isTrial
              ? domainSettingsCopy.headingTrialSubscriptionDomain
              : domainSettingsCopy.headingActiveSubscriptionDomain}
          </DialogTitle>
        </DialogHeader>

        <CardContent className="flex flex-col gap-5 p-0">
          {apiError && <Alert variant="destructive" title={apiError} />}

          {successMessage && <Alert variant="success" title={successMessage} />}

          {showSuccessEndTrialBanner && (
            <UnlockFullQuotaSuccessBanner source="searchPage" />
          )}

          {!isTrial && (
            <div className="w-full p-3 bg-[#EAECF0] rounded-md text-sm text-gray-700">
              <ul className="!list-disc list-inside space-y-1">
                <li className="space-x-1">
                  <span className="text-sm font-semibold">
                    {domainSettingsCopy.websiteDomain}
                  </span>
                  <span className="text-sm font-normal">
                    {domainSettingsCopy.websiteDomainExample}
                  </span>
                </li>
                <li className="space-x-1">
                  <span className="text-sm font-semibold">
                    {domainSettingsCopy.allowedTopLevelDomains}
                  </span>
                  <span className="text-sm font-normal">
                    {domainSettingsCopy.allowedTlds}
                  </span>
                </li>
                <li className="space-x-1">
                  <span className="text-sm font-semibold">
                    {domainSettingsCopy.autmaticSetup}
                  </span>
                  <span className="text-sm font-normal">
                    {domainSettingsCopy.automaticSetupSubtext}
                  </span>
                </li>
                <li className="space-x-1">
                  <span className="text-sm font-semibold">
                    {domainSettingsCopy.protectYourBrand}
                  </span>
                  <span className="text-sm font-normal">
                    {domainSettingsCopy.protectYourBrandSubText}{" "}
                    <button
                      type="button"
                      className="text-blue-500 underline"
                      onClick={() =>
                        window.open(domainSetupKnowledgeBaseUrl, "_blank")
                      }
                    >
                      {domainSettingsCopy.learnWhyWeDoThis}
                    </button>
                  </span>
                </li>
              </ul>
            </div>
          )}

          {isTrial && (
            <div className="text-sm font-semibold">
              {domainSettingsCopy.usingSharedDomainSubtext}
            </div>
          )}
          <Label className="font-semibold">
            {domainSettingsCopy.senderIdentity}
          </Label>
          <div className="flex flex-col gap-1">
            <div className="flex flex-row gap-4">
              <div className="flex flex-col flex-1 gap-2">
                <Label htmlFor="firstName">
                  {domainSettingsCopy.labelFirstName}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  placeholder="Enter first name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="flex flex-col flex-1 gap-2">
                <Label htmlFor="lastName">
                  {domainSettingsCopy.labelLastName}{" "}
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  placeholder="Enter last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
            <span className="text-sm font-normal text-gray-700">
              {domainSettingsCopy.labelNoteForMailbox}
            </span>
            {nameError && <p className="text-sm text-red-500">{nameError}</p>}
          </div>

          <div className="flex flex-col gap-3">
            <Label htmlFor="domain">
              {isTrial
                ? domainSettingsCopy.labelSharedDomainForTrial
                : domainSettingsCopy.labelEnterYourPreferredDomain}
            </Label>
            <div className="flex flex-row items-center gap-3">
              <InputGroup>
                <InputGroupInput
                  id="domain"
                  className={clsx("disabled:text-gray-900 !opacity-100")}
                  placeholder="Type your domain"
                  value={domain}
                  onChange={handleDomainChange}
                  onBlur={handleWebsiteBlur}
                  onPaste={handleWebsitePaste}
                  autoComplete="off"
                  disabled={isTrial || !!successMessage}
                />
                {isTrial && (
                  <InputGroupAddon>
                    <LockIcon
                      height={20}
                      width={20}
                      className="text-gray-700"
                    />
                  </InputGroupAddon>
                )}
              </InputGroup>
            </div>

            {domainError && (
              <p className="text-sm text-red-500">{domainError}</p>
            )}
          </div>

          <TooltipProvider>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm">
                  {domainSettingsCopy.labelNotifiedTo}
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle
                      size={16}
                      className="text-gray-400 cursor-pointer"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {domainSettingsCopy.notifiedToTooltip}
                  </TooltipContent>
                </Tooltip>
              </div>
              <InputGroup>
                <InputGroupInput
                  className={clsx("disabled:text-gray-900 !opacity-100")}
                  value={userNotifyTo}
                  disabled
                />
                <InputGroupAddon>
                  <LockIcon height={20} width={20} className="text-gray-700" />
                </InputGroupAddon>
              </InputGroup>
              <span className="text-sm font-normal text-gray-700">
                {domainSettingsCopy.subtextNotifedTo}
              </span>
            </div>
          </TooltipProvider>
        </CardContent>

        {isTrial && !successMessage && (
          <div className="flex flex-col items-center gap-3 pt-4 mt-2 border-t border-gray-200 mb-4">
            <div className="flex items-center w-full gap-3 text-[--primary-700]">
              <div className="flex-1 h-px bg-[--primary-700]" />
              <span className="text-lg font-semibold">OR</span>
              <div className="flex-1 h-px bg-[--primary-700]" />
            </div>

            <UnlockFullQuotaBanner
              userData={userData}
              currentSubscriptionDetails={subscriptionDetails}
              source={TRIAL_USER_SOURCE.DOMAIN_SETTINGS_DIALOG}
              onUnlockSuccess={() => {
                handleSubscriptionActivatedSuccesfully();
              }}
            />
          </div>
        )}

        <DialogFooter>
          {isTrial || successMessage ? (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {domainSettingsCopy.buttonWillDoThisLater}
              </Button>
              <Button
                variant={"primary"}
                onClick={
                  isTrial ? () => handleSave() : () => onOpenChange(false)
                }
                disabled={isSaving || !firstName || !lastName}
              >
                {isTrial ? "Continue with Trial Domain" : "Okay"}
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                {domainSettingsCopy.buttonWillDoThisLater}
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={
                  isSaving ||
                  !!domainError ||
                  !domain ||
                  !firstName ||
                  !lastName
                }
              >
                {domainSettingsCopy.buttonRegisterAndCreate}
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DomainSettingsDialog;
